/* ==========================================================================
   The SoArt — Delivery rules + step-2 controller
   soart-delivery.js
   --------------------------------------------------------------------------
   This file owns:

     1. SOART.config
        A single, well-documented configuration object. These are the JS
        DEFAULTS. Any data-* attribute emitted by snippets/soart-delivery-modal
        .liquid overrides the matching default at runtime (config = "both":
        JS defaults + theme-editor/Liquid overrides).

     2. SOART.Delivery
        - resolveConfig(el)   merge defaults with the modal's data-* attributes
        - date rules          no same-day, minimum = tomorrow (configurable),
                              blocked weekdays, blocked dates, holiday rules
                              (Christmas / Mothering Sunday / Valentine's …)
        - earliest/max date, isDeliverable(), formatDate()
        - mount(modal)        wires step 2 (postcode check → date + time),
                              toggling the modal's Add-to-cart gate
        - FUTURE-READY hooks  delivery capacity, time-slot limits, local
                              pickup and click & collect (documented stubs)

   No DOM globals leak; the only global is window.SOART.
   ========================================================================== */

(function () {
  'use strict';

  var SOART = (window.SOART = window.SOART || {});

  /* Minimal util fallback in case this file is ever loaded without the core. */
  var util =
    SOART.util ||
    {
      qs: function (s, c) {
        return (c || document).querySelector(s);
      },
      qsa: function (s, c) {
        return Array.prototype.slice.call((c || document).querySelectorAll(s));
      },
      parseList: function (v) {
        if (!v) return [];
        return String(v)
          .split(',')
          .map(function (x) {
            return x.trim();
          })
          .filter(function (x) {
            return x.length > 0;
          });
      }
    };

  /* ======================================================================
     Date helpers (timezone-safe: local midnight, never UTC parsing)
     ====================================================================== */
  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function toISO(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function parseISO(value) {
    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  function today() {
    var n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }

  function addDays(d, n) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  }

  /* Current date in Europe/London (auto GMT/BST), independent of the
     visitor's device timezone. Falls back to device time if Intl is missing. */
  function londonNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date());
      var map = {};
      for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;
      return {
        date: new Date(Number(map.year), Number(map.month) - 1, Number(map.day))
      };
    } catch (err) {
      var n = new Date();
      return { date: new Date(n.getFullYear(), n.getMonth(), n.getDate()) };
    }
  }

  /* Gregorian Easter Sunday (Anonymous Gregorian algorithm / computus). */
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var mm = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * mm + 114) / 31);
    var day = ((h + l - 7 * mm + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  /* The n-th <weekday> of a month, e.g. 2nd Sunday of May. */
  function nthWeekday(year, monthIndex, weekday, n) {
    var first = new Date(year, monthIndex, 1);
    var offset = (7 + weekday - first.getDay()) % 7;
    return new Date(year, monthIndex, 1 + offset + (n - 1) * 7);
  }

  /* ======================================================================
     Holiday preset resolvers — each returns ['YYYY-MM-DD', …] for a year
     ====================================================================== */
  var holidayPresets = {
    /** Fixed calendar date, e.g. fixed(12, 25) → Christmas Day. */
    fixed: function (month, day) {
      return function (year) {
        return [toISO(new Date(year, month - 1, day))];
      };
    },
    /** Valentine's Day — 14 February. */
    valentines: function (year) {
      return [toISO(new Date(year, 1, 14))];
    },
    /** Mothering Sunday (UK) — the fourth Sunday of Lent = Easter − 21 days. */
    motheringSundayUK: function (year) {
      return [toISO(addDays(easterSunday(year), -21))];
    },
    /** Mother's Day (US) — second Sunday of May. */
    mothersDayUS: function (year) {
      return [toISO(nthWeekday(year, 4, 0, 2))];
    }
  };

  /* ======================================================================
     SOART.config — DEFAULTS (edit here, or override via data-* attributes)
     ====================================================================== */
  SOART.config = SOART.config || {
    /* Earliest deliverable = today + minLeadDays. 1 = tomorrow (no same-day). */
    minLeadDays: 1,
    /* How far ahead the date picker allows. */
    maxLeadDays: 60,

    /* Delivery areas (UK postcode areas). */
    allowedPostcodeAreas: ['N', 'NW', 'E', 'EN', 'EC', 'W', 'WC', 'SE', 'SW'],

    /* Weekdays we never deliver on. JS getDay(): 0=Sun, 1=Mon … 6=Sat.
       e.g. [0] blocks Sundays, [0, 1] blocks Sun + Mon. Default: none. */
    blockedWeekdays: [],

    /* One-off blocked dates as ISO strings, e.g. ['2026-08-25']. */
    blockedDates: [],

    /* Holiday rules. Each entry: { name, block, note?, resolve(year) }.
       `block: true` makes the resolved date(s) unselectable. Flip `block`
       to false to keep the date open (e.g. peak trading days). Extend this
       list freely — the resolver just needs to return ISO date strings. */
    holidays: [
      { name: "New Year's Day", block: true, resolve: holidayPresets.fixed(1, 1) },
      { name: "Valentine's Day", block: false, note: 'Peak trading — order early', resolve: holidayPresets.valentines },
      { name: 'Mothering Sunday (UK)', block: false, note: 'Peak trading — order early', resolve: holidayPresets.motheringSundayUK },
      { name: 'Christmas Day', block: true, resolve: holidayPresets.fixed(12, 25) },
      { name: 'Boxing Day', block: true, resolve: holidayPresets.fixed(12, 26) }
    ],

    /* Delivery time slots (single source of truth at runtime). */
    timeSlots: [
      { value: '12-3', label: '12pm – 3pm' },
      { value: '3-6', label: '3pm – 6pm' }
    ],

    /* User-facing copy (all overridable). */
    messages: {
      postcode: {
        empty: 'Please enter your delivery postcode.',
        format: 'Please enter a valid UK postcode.',
        rejected: 'Sorry, we do not deliver here.',
        accepted: 'Great! We deliver to your postcode.'
      },
      date: {
        past: 'Sorry, the earliest we can deliver is {earliest}.',
        max: 'Please choose a date within the next {days} days.',
        weekday: 'We’re not delivering on that day. Please choose another date.',
        blocked: 'That date is unavailable. Please choose another.',
        holiday: 'Unavailable — {name}. Please choose another date.',
        format: 'Please choose a valid date.',
        ok: 'Lovely — delivery available on {date}.'
      }
    },

    /* Locale for date formatting (falls back to <html lang> then en-GB). */
    locale: null,

    /* --------------------------------------------------------------------
       FUTURE-READY (not yet enforced — documented extension points)
       --------------------------------------------------------------------
       capacity:  map or provider of remaining capacity, e.g.
                    { '2026-12-24': { '9-12': 0, '12-3': 5 } }
       slotLimits: global per-slot caps, e.g. { '9-12': 20 }
       pickup / clickAndCollect: alternative fulfilment methods that can
                    bypass the postcode gate and use their own date rules.
       ------------------------------------------------------------------ */
    capacity: null,
    slotLimits: null,
    pickup: { enabled: false, location: null },
    clickAndCollect: { enabled: false, locations: [] }
  };

  /* ======================================================================
     SOART.Delivery
     ====================================================================== */
  function interpolate(template, vars) {
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return vars && vars[key] != null ? vars[key] : '';
    });
  }

  function setResult(el, text, kind) {
    if (!el) return;
    el.textContent = text || '';
    el.classList.remove('is-success', 'is-error');
    if (kind === 'success') el.classList.add('is-success');
    else if (kind === 'error') el.classList.add('is-error');
  }

  function renderTimeSlots(select, slots) {
    if (!select || !slots || !slots.length) return;
    select.innerHTML = '';
    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.textContent = 'Choose a time slot…';
    select.appendChild(placeholder);
    slots.forEach(function (slot) {
      var opt = document.createElement('option');
      opt.value = slot.value;
      opt.textContent = slot.label;
      select.appendChild(opt);
    });
  }

  var Delivery = {
    holidayPresets: holidayPresets,

    /* Expose helpers (useful for tests / future modules). */
    _dateUtils: {
      toISO: toISO,
      parseISO: parseISO,
      today: today,
      addDays: addDays,
      easterSunday: easterSunday,
      nthWeekday: nthWeekday
    },

    /** Locale used for human-readable dates. */
    _locale: function () {
      return (
        SOART.config.locale ||
        (typeof document !== 'undefined' &&
          document.documentElement &&
          document.documentElement.getAttribute('lang')) ||
        'en-GB'
      );
    },

    /** Merge SOART.config defaults with a modal element's data-* overrides. */
    resolveConfig: function (el) {
      if (el && el._soartResolvedConfig) return el._soartResolvedConfig;

      var base = SOART.config;
      var cfg = {
        minLeadDays: base.minLeadDays,
        maxLeadDays: base.maxLeadDays,
        allowedPostcodeAreas: base.allowedPostcodeAreas.slice(),
        blockedWeekdays: base.blockedWeekdays.slice(),
        blockedDates: base.blockedDates.slice(),
        holidays: base.holidays.slice(),
        timeSlots: base.timeSlots.slice(),
        messages: base.messages,
        locale: base.locale,
        capacity: base.capacity,
        slotLimits: base.slotLimits,
        pickup: base.pickup,
        clickAndCollect: base.clickAndCollect
      };

      if (el && el.dataset) {
        var d = el.dataset;
        if (d.soartMinLeadDays != null && d.soartMinLeadDays !== '') {
          cfg.minLeadDays = parseInt(d.soartMinLeadDays, 10);
        }
        if (d.soartMaxLeadDays != null && d.soartMaxLeadDays !== '') {
          cfg.maxLeadDays = parseInt(d.soartMaxLeadDays, 10);
        }
        if (d.soartAllowedAreas) {
          cfg.allowedPostcodeAreas = util.parseList(d.soartAllowedAreas).map(function (a) {
            return a.toUpperCase();
          });
        }
        if (d.soartBlockedWeekdays) {
          cfg.blockedWeekdays = util
            .parseList(d.soartBlockedWeekdays)
            .map(function (n) {
              return parseInt(n, 10);
            })
            .filter(function (n) {
              return n >= 0 && n <= 6;
            });
        }
        if (d.soartBlockedDates) {
          cfg.blockedDates = util.parseList(d.soartBlockedDates);
        }
        if (d.soartTimeSlots) {
          try {
            var slots = JSON.parse(d.soartTimeSlots);
            if (Array.isArray(slots) && slots.length) cfg.timeSlots = slots;
          } catch (err) {
            /* invalid JSON — keep defaults */
          }
        }
      }

      /* Sanitise numeric bounds. */
      if (!isFinite(cfg.minLeadDays) || cfg.minLeadDays < 0) cfg.minLeadDays = base.minLeadDays;
      if (!isFinite(cfg.maxLeadDays) || cfg.maxLeadDays < cfg.minLeadDays) {
        cfg.maxLeadDays = Math.max(cfg.minLeadDays, base.maxLeadDays);
      }

      if (el) el._soartResolvedConfig = cfg;
      return cfg;
    },

    /** Does any BLOCKING holiday fall on this date? Returns the holiday or null. */
    matchBlockingHoliday: function (dateObj, config) {
      var iso = toISO(dateObj);
      var year = dateObj.getFullYear();
      var holidays = config.holidays || [];
      for (var i = 0; i < holidays.length; i++) {
        var h = holidays[i];
        if (!h || !h.block || typeof h.resolve !== 'function') continue;
        var dates;
        try {
          dates = h.resolve(year) || [];
        } catch (err) {
          dates = [];
        }
        if (dates.indexOf(iso) !== -1) return h;
      }
      return null;
    },

    /** Earliest deliverable date (skips blocked weekdays/dates/holidays). */
    earliestDeliverableDate: function (config) {
      var start = addDays(londonNow().date, Math.max(0, config.minLeadDays));
      var cursor = start;
      for (var i = 0; i < 400; i++) {
        if (this.isDeliverable(cursor, config).ok) return cursor;
        cursor = addDays(cursor, 1);
      }
      return start;
    },

    /** Latest selectable date. */
    maxDate: function (config) {
      return addDays(today(), config.maxLeadDays);
    },

    /**
     * Is a date deliverable?
     * @returns {{ok:boolean, reason:string, holiday?:Object}}
     *          reason ∈ 'ok' | 'format' | 'past' | 'max' | 'weekday'
     *                   | 'blocked' | 'holiday'
     */
    isDeliverable: function (value, config) {
      var d = parseISO(value);
      if (!d) return { ok: false, reason: 'format' };

      var min = addDays(londonNow().date, Math.max(0, config.minLeadDays));
      if (d < min) return { ok: false, reason: 'past' };

      var max = addDays(today(), config.maxLeadDays);
      if (d > max) return { ok: false, reason: 'max' };

      if ((config.blockedWeekdays || []).indexOf(d.getDay()) !== -1) {
        return { ok: false, reason: 'weekday' };
      }

      if ((config.blockedDates || []).indexOf(toISO(d)) !== -1) {
        return { ok: false, reason: 'blocked' };
      }

      var holiday = this.matchBlockingHoliday(d, config);
      if (holiday) return { ok: false, reason: 'holiday', holiday: holiday };

      return { ok: true, reason: 'ok' };
    },

    /** Human-readable date, e.g. "Wednesday, 15 July 2026". */
    formatDate: function (value) {
      var d = parseISO(value);
      if (!d) return String(value == null ? '' : value);
      try {
        return new Intl.DateTimeFormat(this._locale(), {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }).format(d);
      } catch (err) {
        return toISO(d);
      }
    },

    /** Build the status message for a date check result. */
    describeDate: function (check, iso, config) {
      var msgs = config.messages.date;
      switch (check.reason) {
        case 'past':
          return interpolate(msgs.past, {
            earliest: this.formatDate(toISO(this.earliestDeliverableDate(config)))
          });
        case 'max':
          return interpolate(msgs.max, { days: config.maxLeadDays });
        case 'weekday':
          return msgs.weekday;
        case 'blocked':
          return msgs.blocked;
        case 'holiday':
          return interpolate(msgs.holiday, { name: check.holiday ? check.holiday.name : '' });
        case 'format':
          return msgs.format;
        case 'ok':
          return interpolate(msgs.ok, { date: this.formatDate(iso) });
        default:
          return '';
      }
    },

    /* --------------------------------------------------------------------
       FUTURE-READY stubs — safe no-ops today, real hooks for later.
       -------------------------------------------------------------------- */

    /** Remaining capacity for a slot. Override config.capacity to enforce. */
    getSlotAvailability: function (iso, slotValue, config) {
      var capacity = config && config.capacity;
      if (capacity && capacity[iso] && typeof capacity[iso][slotValue] === 'number') {
        var remaining = capacity[iso][slotValue];
        return { remaining: remaining, available: remaining > 0 };
      }
      return { remaining: Infinity, available: true };
    },

    /** Slots available for a date (currently all; filter here for limits). */
    availableSlots: function (iso, config) {
      var self = this;
      return (config.timeSlots || []).filter(function (slot) {
        return self.getSlotAvailability(iso, slot.value, config).available;
      });
    },

    /* --------------------------------------------------------------------
       mount(modal) — wire step 2 to a <soart-delivery-modal> instance.
       Called by the modal on every open: listeners bind ONCE, state resets
       every time.
       -------------------------------------------------------------------- */
    mount: function (modal) {
      var self = this;
      var config = this.resolveConfig(modal);
      modal._soartConfig = config;
      modal._soartState = modal._soartState || { postcodeOk: false, dateOk: false };

      var postcodeEl = util.qs('[data-soart-field="postcode"]', modal);
      var checkBtn = util.qs('[data-soart-check]', modal);
      var postcodeResult = util.qs('[data-soart-postcode-result]', modal);
      var reveal = util.qs('[data-soart-delivery-details]', modal);
      var dateEl = util.qs('[data-soart-field="date"]', modal);
      var dateResult = util.qs('[data-soart-date-result]', modal);
      var timeEl = util.qs('[data-soart-field="time"]', modal);

      if (!postcodeEl || !dateEl || !timeEl) return; /* markup missing */

      var earliest = this.earliestDeliverableDate(config);

      /* Configure the native date input. */
      dateEl.min = toISO(earliest);
      dateEl.max = toISO(this.maxDate(config));

      /* Rebuild the time select from config (single source of truth). */
      renderTimeSlots(timeEl, config.timeSlots);

      function updateGate() {
        var s = modal._soartState;
        var timeOk = !!timeEl.value;
        if (s.postcodeOk && s.dateOk && timeOk) {
          modal.enableSubmit();
        } else {
          modal.disableSubmit();
        }
      }

      function validateDate() {
        var iso = dateEl.value;
        if (!iso) {
          modal._soartState.dateOk = false;
          setResult(dateResult, '', null);
          dateEl.classList.remove('is-invalid');
          dateEl.removeAttribute('aria-invalid');
          updateGate();
          return;
        }
        var check = self.isDeliverable(iso, config);
        setResult(dateResult, self.describeDate(check, iso, config), check.ok ? 'success' : 'error');
        dateEl.classList.toggle('is-invalid', !check.ok);
        dateEl.setAttribute('aria-invalid', check.ok ? 'false' : 'true');
        modal._soartState.dateOk = check.ok;
        updateGate();
      }

      function checkPostcode() {
        var res = SOART.Postcode.validate(postcodeEl.value, {
          allowedAreas: config.allowedPostcodeAreas,
          messages: config.messages.postcode
        });
        setResult(postcodeResult, res.message, res.ok ? 'success' : 'error');
        postcodeEl.classList.toggle('is-invalid', !res.ok);
        postcodeEl.setAttribute('aria-invalid', res.ok ? 'false' : 'true');
        modal._soartState.postcodeOk = res.ok;

        if (res.ok) {
          postcodeEl.value = SOART.Postcode.format(postcodeEl.value);
          reveal.hidden = false;
          if (!dateEl.value) dateEl.value = toISO(earliest);
          validateDate();
          requestAnimationFrame(function () {
            try {
              dateEl.focus();
            } catch (err) {
              /* ignore */
            }
          });
        } else {
          reveal.hidden = true;
        }
        updateGate();
      }

      /* Reset transient state on every open (keeps typed postcode text). */
      function reset() {
        modal._soartState.postcodeOk = false;
        modal._soartState.dateOk = false;
        setResult(postcodeResult, '', null);
        postcodeEl.classList.remove('is-invalid');
        postcodeEl.removeAttribute('aria-invalid');
        reveal.hidden = true;
        setResult(dateResult, '', null);
        dateEl.classList.remove('is-invalid');
        dateEl.removeAttribute('aria-invalid');
        dateEl.value = '';
        timeEl.selectedIndex = 0;
        modal.disableSubmit();
      }

      /* Bind listeners once. */
      if (!modal._deliveryWired) {
        checkBtn.addEventListener('click', checkPostcode);

        postcodeEl.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            checkPostcode();
          }
        });

        /* Editing the postcode after a pass invalidates it until re-checked. */
        postcodeEl.addEventListener('input', function () {
          if (modal._soartState.postcodeOk) {
            modal._soartState.postcodeOk = false;
            reveal.hidden = true;
            setResult(postcodeResult, '', null);
            postcodeEl.removeAttribute('aria-invalid');
            modal.disableSubmit();
          }
        });

        dateEl.addEventListener('change', validateDate);
        dateEl.addEventListener('input', validateDate);
        timeEl.addEventListener('change', updateGate);

        modal._deliveryWired = true;
      }

      reset();
    }
  };

  SOART.Delivery = SOART.Delivery || Delivery;
})();